<!-- 右侧歌曲详情侧边栏组件 -->
<script lang="ts" setup>
import { onMounted, onBeforeUnmount } from 'vue'
import type { ParsedClipItem } from '~/types/home'
import { useRightSongsDetailStore } from '~/stores/rightSongsDetailStore'
import { useToggleUpvote, useGenerateAction } from '~/composables/useCommonFunction'
import { useShareModalStore } from '~/stores/shareModalStore'
import SongActionsMenu from '~/components/SongMoreActionsPopover.vue'
import HomePlay from '~/assets/icons/homePlay.svg?component'
import HomeUpvote from '~/assets/icons/homeUpvote.svg?component'
import HomeUnUpvote from '~/assets/icons/homeUnUpvote.svg?component'
import HomeShare from '~/assets/icons/homeShare.svg?component'
import HomeMore from '~/assets/icons/homeMore.svg?component'
import MoreActionGenerateSimilar from '~/assets/icons/moreActionGenerateSimilar.svg?component'

const props = defineProps<{
  songActionsExcludeIds?: string[]
}>()

const { requireAuth } = useAuthGuard()
const rightSongsDetailStore = useRightSongsDetailStore()
const shareModalStore = useShareModalStore()

// 点赞 / 取消点赞
const { handleToggleUpvote: handleToggleUpvoteBase } = useToggleUpvote<ParsedClipItem>((uuid, updates) => {
  if (rightSongsDetailStore.songData) {
    // 创建新对象以确保响应式更新
    rightSongsDetailStore.songData = {
      ...rightSongsDetailStore.songData,
      isVote: updates.isVote,
      upvote_count: updates.upvote_count
    }
  }
})

const openShare = () => {
  if (!song.value) return
  const title = song.value.metadata?.type === 'upload' && song.value.title === 'my_2024-09-04_01_37_04' && song.value.fullPrompt?.title || song.value.title || song.value.metadata?.prompt || `Untitled - ${formatDate(song.value.createdAt)}`

  shareModalStore.openShare({
    title,
    uuid: song.value.uuid
  })
}

// generate similar
const { handleGenerateAction } = useGenerateAction<ParsedClipItem>()
const handleGenerateSimilar = async () => {
  if (!song.value) return
  await handleGenerateAction('generate_similar', song.value)
}

// 从 store 获取歌曲数据
const song = computed(() => rightSongsDetailStore.songData as ParsedClipItem | null)

// 歌词文本：优先使用 fullPrompt.lyrics，其次 metadata.prompt
const lyricsText = computed(() => {
  const s = song.value
  if (!s) return ''
  return s.fullPrompt?.lyrics || s.metadata?.prompt || ''
})
const isOpen = computed(() => rightSongsDetailStore.isOpen)

// 判断是否排除了 publish 功能
const isPublishExcluded = computed(() => {
  return props.songActionsExcludeIds?.includes('publish') ?? false
})

// 判断是否排除了 move_to_trash 功能
const isDeleteExcluded = computed(() => {
  return props.songActionsExcludeIds?.includes('move_to_trash') ?? false
})

const handleEditSuccess = (payload: { title: string, lyrics: string }) => {
  if (!rightSongsDetailStore.songData) return
  rightSongsDetailStore.songData = {
    ...rightSongsDetailStore.songData,
    title: payload.title?.trim() || rightSongsDetailStore.songData.title,
    metadata: {
      ...rightSongsDetailStore.songData.metadata,
      prompt: payload.lyrics
    }
  }
}

// 播放量全局更新事件监听：当当前详情歌曲的 uuid 匹配时，本地将 play_count +1
const handlePlayCountUpdated = (event: Event) => {
  const customEvent = event as CustomEvent<{ uuid?: string }>
  const uuid = customEvent.detail?.uuid
  const songData = rightSongsDetailStore.songData
  if (!uuid || !songData || songData.uuid !== uuid) return

  const prev = typeof songData.play_count === 'number' ? songData.play_count : 0
  rightSongsDetailStore.songData = {
    ...songData,
    play_count: prev + 1
  }
}

// 点赞状态全局更新事件监听：当当前详情歌曲的 uuid 匹配时，同步 isVote 和 upvote_count
const handleUpvoteUpdated = (event: Event) => {
  const customEvent = event as CustomEvent<{ uuid?: string, isVote?: number, upvote_count?: number }>
  const detail = customEvent.detail
  const songData = rightSongsDetailStore.songData
  if (!detail?.uuid || !songData || songData.uuid !== detail.uuid) return

  rightSongsDetailStore.songData = {
    ...songData,
    isVote: typeof detail.isVote === 'number' ? detail.isVote : songData.isVote,
    upvote_count: typeof detail.upvote_count === 'number' ? detail.upvote_count : songData.upvote_count
  }
}

onMounted(() => {
  if (typeof window === 'undefined') return
  window.addEventListener('song-play-count-updated', handlePlayCountUpdated as EventListener)
  window.addEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
})

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return
  window.removeEventListener('song-play-count-updated', handlePlayCountUpdated as EventListener)
  window.removeEventListener('song-upvote-updated', handleUpvoteUpdated as EventListener)
})

// Style 部分是否展开
const styleExpanded = ref(true)

// 关闭侧边栏
const handleClose = () => {
  rightSongsDetailStore.closeLeftSongsDetail()
}

const handleToggleUpvote = async () => {
  if (!song.value) return
  await handleToggleUpvoteBase(song.value)
}

// 模型版本映射
const modelVersionMap = [
  { value: 'v5', label: 'v7' },
  { value: 'v4.5+', label: 'V6' },
  { value: 'v4.5', label: 'v5' },
  { value: 'v4', label: 'v4' }
]

// 根据 value 获取对应的 label
const getModelVersionLabel = (value: string | undefined | null): string => {
  if (!value) return ''
  const normalizedValue = value.toLowerCase()
  const mapped = modelVersionMap.find(item => item.value.toLowerCase() === normalizedValue)
  return mapped?.label || value
}

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}
</script>

<template>
  <div
    v-if="isOpen && song"
    class="flex flex-col gap-y-[16px] relative w-full h-full bg-[#262635] px-[12px] py-[16px] hidden md:block overflow-y-auto"
  >
    <!-- 右上角关闭按钮 -->
    <button
      class="absolute top-[10px] right-[10px] flex items-center justify-center size-[32px] bg-[#13131899] rounded-[8px] cursor-pointer z-10 group"
      @click="handleClose"
    >
      <div
        class="flex items-center justify-center w-[20px] h-[20px] cursor-pointer
        before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] group-hover:before:bg-[#E7E7EC] before:rotate-45 before:rounded-[20px]
        after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] group-hover:after:bg-[#E7E7EC] after:-rotate-45 after:rounded-[20px]"
      />
    </button>

    <!-- 顶部封面图 -->
    <div class="relative size-[200px] overflow-hidden">
      <img
        :src="song.fileInfo?.cosUrl"
        class="size-full object-cover scale-[1.1]"
        alt="Song cover"
      >
      <!-- 右下角版本标签 -->
      <span class="absolute bottom-[8px] right-[10px] text-[12px] text-[#B8B8C7] leading-[16px] bg-[#13131899] border border-[#8888A1] px-[4px]  rounded-[4px]">
        {{ getModelVersionLabel(song.metadata?.model_badges?.songrow?.display_name) }}
      </span>
    </div>

    <!-- 歌曲标题、交互控件 -->
    <div class="flex flex-col gap-y-[8px]">
      <!-- 歌曲标题 -->
      <h1 class="text-[16px] font-bold text-white leading-[24px]">
        {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}` }}
      </h1>

      <!-- 交互控件 -->
      <div class="flex items-center justify-between">
        <!-- 播放数 -->
        <div class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] hover:bg-[#383847] rounded-[4px] cursor-pointer">
          <HomePlay
            class="size-[16px] m-[4px]"
          />
          <span class="pr-[4px]">{{ song.play_count || 0 }}</span>
        </div>

        <!-- 点赞数 -->
        <div
          class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] leading-[24px] hover:bg-[#383847] rounded-[4px] cursor-pointer"
          @click="requireAuth(() => handleToggleUpvote())"
        >
          <component
            :is="song?.isVote === 1 ? HomeUpvote : HomeUnUpvote"
            :class="['size-[16px] m-[4px]', song?.isVote === 1 ? 'text-[#F24B4B]' : 'text-[#8888A1]']"
          />
          <span class="pr-[4px]">{{ song.upvote_count || 0 }}</span>
        </div>

        <!-- 分享按钮 -->
        <div
          class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] hover:bg-[#383847] rounded-[4px] cursor-pointer"
          @click="requireAuth(() => openShare())"
        >
          <HomeShare
            class="size-[16px] m-[4px]"
          />
        </div>

        <!-- Generate similar -->
        <button
          class="flex items-center text-[12px] text-[#8888A1] hover:text-[#E7E7EC] hover:bg-[#383847] rounded-[4px] cursor-pointer"
          @click="requireAuth(() => handleGenerateSimilar())"
        >
          <MoreActionGenerateSimilar
            class="size-[16px] m-[4px]"
          />
        </button>

        <!-- 更多选项 -->
        <div
          @click="requireAuth(() => {})"
        >
          <SongActionsMenu
            :song="song"
            :exclude-ids="props.songActionsExcludeIds"
            v-bind="isPublishExcluded ? {} : {
              'onUpdate:isPublic': (value: number) => {
                if (rightSongsDetailStore.songData) {
                  rightSongsDetailStore.songData = {
                    ...rightSongsDetailStore.songData,
                    is_public: value
                  }
                }
              }
            }"
            v-on="isDeleteExcluded ? {} : {
              deleted: () => {
                // 删除后关闭详情页
                rightSongsDetailStore.closeLeftSongsDetail()
              }
            }"
            @edit-success="handleEditSuccess"
          >
            <div class="flex items-center text-[#B8B8C7] hover:text-[#E7E7EC] hover:bg-[#383847] rounded-[4px] ml-auto cursor-pointer">
              <HomeMore class="size-[24px]" />
            </div>
          </SongActionsMenu>
        </div>
      </div>
    </div>

    <!-- Style 部分 -->
    <div class="flex flex-col gap-y-[8px]">
      <button
        class="flex items-center gap-x-[4px] text-[14px] text-[#E7E7EC] leading-[20px] cursor-pointer"
        @click="styleExpanded = !styleExpanded"
      >
        <UIcon
          :name="styleExpanded ? 'material-symbols:keyboard-arrow-down' : 'material-symbols:keyboard-arrow-right'"
          class="size-[10px]"
        />
        <span>Style</span>
      </button>

      <div
        v-if="styleExpanded"
        class="max-h-[100px] text-[12px] text-[#8888A1] leading-[16px] pl-[16px] overflow-y-auto"
      >
        {{ song.metadata?.tags || '(no styles)' }}
      </div>
    </div>

    <!-- Lyrics 部分 -->
    <div class="flex flex-col gap-y-[8px] flex-1 overflow-hidden">
      <h2 class="text-[14px] text-[#E7E7EC] line-clamp-1">
        {{ song.metadata?.type === 'upload' && song.title === 'my_2024-09-04_01_37_04' && song.fullPrompt?.title || song.title || song.metadata?.prompt || `Untitled - ${formatDate(song.createdAt)}` }}
      </h2>

      <!-- 直接显示传入歌曲的歌词文本 -->
      <div class="flex-1 text-[15px] text-[#E7E7EC] leading-[24px] overflow-y-auto whitespace-pre-wrap">
        <template v-if="lyricsText">
          {{ lyricsText }}
        </template>
        <template v-else>
          <div class="text-[14px] text-[#5E5E77]">
            No lyrics available
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
